import React, { useState, useEffect } from 'react';

import { MINIMUM_PAGE_COUNT } from 'constants/page';

import * as userService from 'services/user';
import * as skillService from 'services/skillService';
import * as designationService from 'services/designation';

import useQuery from 'hooks/useQuery';
import useTitle from 'hooks/useTitle';
import { handleError } from 'utils/errorHandler';

import Pagination from 'components/common/paginator';
import Loading from 'components/common/loading/Loading';
import { ResourceFilterV1, ResourceResultsV1 } from './components';

import type { QueryObject, RouterProps } from 'interface/queryParam';

import type { DesignationRecord, ResourceEmployeeV1, ResourceFinderV1LoadingState } from './types';

const PAGE_COUNT = 10;

const ResourceFinderV1: React.FC<RouterProps> = (props) => {
  const [query, setQuery] = useQuery(props) as [QueryObject, (next: QueryObject) => void];
  const [loading, setLoading] = useState<ResourceFinderV1LoadingState>({
    employees: false,
    skills: false,
    designations: false,
    export: false,
  });
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [employees, setEmployees] = useState<ResourceEmployeeV1[]>([]);
  const [skills, setSkills] = useState<DesignationRecord[]>([]);
  const [designations, setDesignations] = useState<DesignationRecord[]>([]);
  const [pageSize, setPageSize] = useState(PAGE_COUNT);

  const setTitle = useTitle();

  useEffect(() => {
    setTitle('People Finder');
  }, [setTitle]);

  const { page, ...filterQuery } = query;

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setLoading((prevLoading) => ({ ...prevLoading, skills: true }));

        const response = await skillService.fetchAllV1();
        const { data } = response;

        setSkills(Array.isArray(data) ? (data as DesignationRecord[]) : []);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, skills: false }));
      }
    };

    fetchSkills();
  }, []);

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        setLoading((prevLoading) => ({ ...prevLoading, designations: true }));

        const response = await designationService.fetchAll();
        const { data } = response;

        setDesignations(Array.isArray(data) ? (data as DesignationRecord[]) : []);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, designations: false }));
      }
    };

    fetchDesignations();
  }, []);

  useEffect(() => {
    if (!page) {
      setQuery({ ...query, page: 1 });

      return;
    }

    const fetchResources = async () => {
      try {
        setLoading((prevLoading) => ({ ...prevLoading, employees: true }));

        const response = await userService.fetchResource<ResourceEmployeeV1>(query);

        const { data } = response;
        const { total: totalCount } = response.meta;

        const computedPageCount = Math.ceil(totalCount / pageSize);

        setTotal(totalCount);
        setEmployees(data);
        setPageCount(computedPageCount);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, employees: false }));
      }
    };

    void fetchResources();
  }, [page, pageSize, query, setQuery]);

  const handlePageChange = (nextPage: number) => {
    setQuery({ ...query, page: nextPage });
  };

  const handleFilterClick = (newFilterState: QueryObject) => {
    setQuery({ ...query, ...newFilterState, page: 1 });
  };

  const handleExportClick = async () => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, export: true }));

      const exportResponse = await userService.exportResource(query);
      const link = document.createElement('a');

      link.href = URL.createObjectURL(exportResponse.data);
      link.download = 'leapfroggers skills.csv';
      link.click();

      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } catch (err) {
      handleError(err as object);
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, export: false }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setQuery({ ...query, size: nextPageSize, page: 1 });
  };

  const handleClearClick = () => {};

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3 className="table-title">People Finder</h3>
            </div>
          </div>
          <div className="full-scope-card__content d-flex position-relative">
            <div className="resource-filter-area">
              <ResourceFilterV1
                skills={skills}
                initialState={filterQuery}
                designations={designations}
                onFilter={handleFilterClick}
                onClear={handleClearClick}
                loadingSkills={loading.skills}
                loadingDesignations={loading.designations}
              />
            </div>
            <div className="resource-results-area">
              {loading.employees ? (
                <Loading />
              ) : (
                <>
                  <ResourceResultsV1
                    employees={employees}
                    total={total}
                    exporting={loading.export}
                    onExport={handleExportClick}
                  />
                  <Pagination
                    pageData={{ page: +String(page ?? '') }}
                    onPageChange={handlePageChange}
                    pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResourceFinderV1;
